import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Loader2, Target, Building2, CalendarDays, CalendarCheck } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { createGoal } from "@/Api/goals";
import { getSelectUnities } from "@/Api/unities";
import { ScrollArea } from "@/components/ui/scroll-area";

const goalSchema = z.object({
  unity_ids: z.array(z.number()).min(1, "Selecione pelo menos uma unidade"),
  start_date: z.string().min(1, "Data de início é obrigatória"),
  end_date: z.string().min(1, "Data de fim é obrigatória"),
  working_days: z.number().min(1, "Selecione pelo menos um dia útil"),
});

type GoalForm = z.infer<typeof goalSchema>;

const dayNames = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const monthNames = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

interface Unity {
  id: number;
  name: string;
}

interface AddGoalProps {
  updateFront: any;
  dataApi?: any;
  refreshList?: () => Promise<void>;
}

export function AddGoal({ refreshList }: AddGoalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [unities, setUnities] = useState<Unity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUnities, setIsLoadingUnities] = useState(false);
  const [selectedUnities, setSelectedUnities] = useState<number[]>([]);
  const [selectedWorkingDays, setSelectedWorkingDays] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<GoalForm>({
    resolver: zodResolver(goalSchema),
    defaultValues: {
      unity_ids: [],
      start_date: "",
      end_date: "",
      working_days: 0,
    },
  });

  const startDate = watch("start_date");
  const endDate = watch("end_date");

  // Gerar lista de dias no período agrupados por mês
  const { daysInPeriod, daysByMonth } = useMemo(() => {
    if (!startDate || !endDate) return { daysInPeriod: [], daysByMonth: [] };

    const start = new Date(startDate + "T00:00:00");
    const end = new Date(endDate + "T00:00:00");

    if (start > end) return { daysInPeriod: [], daysByMonth: [] };

    const days: { date: Date; key: string; dayOfWeek: number }[] = [];
    const current = new Date(start);

    while (current <= end) {
      days.push({
        date: new Date(current),
        key: current.toISOString().split("T")[0],
        dayOfWeek: current.getDay(),
      });
      current.setDate(current.getDate() + 1);
    }

    // Agrupar por mês
    const grouped: { month: number; year: number; days: typeof days }[] = [];
    days.forEach((day) => {
      const month = day.date.getMonth();
      const year = day.date.getFullYear();
      const existing = grouped.find((g) => g.month === month && g.year === year);
      if (existing) {
        existing.days.push(day);
      } else {
        grouped.push({ month, year, days: [day] });
      }
    });

    return { daysInPeriod: days, daysByMonth: grouped };
  }, [startDate, endDate]);

  // Pré-selecionar dias úteis (segunda a sábado) quando as datas mudarem
  useEffect(() => {
    if (daysInPeriod.length > 0) {
      const workingDayKeys = daysInPeriod
        .filter((day) => day.dayOfWeek !== 0) // Exclui domingos
        .map((day) => day.key);

      setSelectedWorkingDays(workingDayKeys);
      setValue("working_days", workingDayKeys.length, { shouldValidate: false });
    } else {
      setSelectedWorkingDays([]);
      setValue("working_days", 0, { shouldValidate: false });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysInPeriod]);

  useEffect(() => {
    const loadUnities = async () => {
      if (!isOpen) return;

      setIsLoadingUnities(true);
      try {
        const response = await getSelectUnities();
        if (response.data?.response) {
          setUnities(response.data.response);
        }
      } catch (error) {
        console.error("Erro ao carregar unidades:", error);
      } finally {
        setIsLoadingUnities(false);
      }
    };

    loadUnities();
  }, [isOpen]);

  const handleUnityToggle = (unityId: number, checked: boolean) => {
    const newSelected = checked
      ? [...selectedUnities, unityId]
      : selectedUnities.filter((id) => id !== unityId);

    setSelectedUnities(newSelected);
    setValue("unity_ids", newSelected, { shouldValidate: false });
  };

  const handleSelectAll = () => {
    if (selectedUnities.length === unities.length) {
      setSelectedUnities([]);
      setValue("unity_ids", [], { shouldValidate: false });
    } else {
      const allIds = unities.map((u) => u.id);
      setSelectedUnities(allIds);
      setValue("unity_ids", allIds, { shouldValidate: false });
    }
  };

  const handleWorkingDayToggle = (dayKey: string, checked: boolean) => {
    const newSelected = checked
      ? [...selectedWorkingDays, dayKey]
      : selectedWorkingDays.filter((key) => key !== dayKey);

    setSelectedWorkingDays(newSelected);
    setValue("working_days", newSelected.length, { shouldValidate: false });
  };

  const handleSelectAllWorkingDays = () => {
    if (selectedWorkingDays.length === daysInPeriod.length) {
      setSelectedWorkingDays([]);
      setValue("working_days", 0, { shouldValidate: false });
    } else {
      const allDays = daysInPeriod.map((d) => d.key);
      setSelectedWorkingDays(allDays);
      setValue("working_days", allDays.length, { shouldValidate: false });
    }
  };

  const handleSelectWeekdays = () => {
    // Seleciona segunda a sábado (exclui domingo = 0)
    const weekdayKeys = daysInPeriod
      .filter((day) => day.dayOfWeek !== 0)
      .map((day) => day.key);

    setSelectedWorkingDays(weekdayKeys);
    setValue("working_days", weekdayKeys.length, { shouldValidate: false });
  };

  const onSubmit = async (data: GoalForm) => {
    setIsLoading(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      for (const unityId of data.unity_ids) {
        const response = await createGoal({
          unity_id: unityId,
          start_date: data.start_date,
          end_date: data.end_date,
          working_days: data.working_days,
          selected_working_days: selectedWorkingDays.length > 0 ? selectedWorkingDays : null,
        });

        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(
          successCount === 1
            ? "Meta criada com sucesso!"
            : `${successCount} metas criadas com sucesso!`
        );
      }

      if (errorCount > 0) {
        toast.error(`${errorCount} meta(s) não puderam ser criadas.`);
      }

      reset();
      setSelectedUnities([]);
      setIsOpen(false);

      if (refreshList) {
        await refreshList();
      }
    } catch (error) {
      toast.error("Erro ao criar meta");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      reset();
      setSelectedUnities([]);
      setSelectedWorkingDays([]);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-primary hover:bg-primary/90">
          <Plus className="mr-2 h-4 w-4" />
          Nova Meta
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Target className="h-4 w-4 text-primary" />
            </div>
            Nova Meta de Vendas
          </DialogTitle>
          <DialogDescription>
            Crie metas de vendas para uma ou mais unidades. Após criar, você poderá
            definir os valores individuais para cada vendedor.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Seção de Unidades */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2 text-base">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                Unidades
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                className="h-7 text-xs"
              >
                {selectedUnities.length === unities.length
                  ? "Desmarcar todas"
                  : "Selecionar todas"}
              </Button>
            </div>
            {isLoadingUnities ? (
              <div className="flex items-center justify-center p-8 border rounded-lg bg-muted/30">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <ScrollArea className="h-[140px] border rounded-lg">
                <div className="p-3 space-y-1">
                  {unities.map((unity) => {
                    const isChecked = selectedUnities.includes(unity.id);
                    return (
                      <div
                        key={unity.id}
                        onClick={() => handleUnityToggle(unity.id, !isChecked)}
                        className={`
                          flex items-center space-x-3 p-2.5 rounded-lg cursor-pointer transition-all
                          ${isChecked
                            ? "bg-primary/10 border border-primary/30"
                            : "hover:bg-muted/50 border border-transparent"
                          }
                        `}
                      >
                        <div
                          className={`
                            h-4 w-4 rounded border-2 flex items-center justify-center transition-colors
                            ${isChecked
                              ? "bg-primary border-primary"
                              : "border-muted-foreground/50"
                            }
                          `}
                        >
                          {isChecked && (
                            <svg
                              className="h-3 w-3 text-primary-foreground"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={3}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="text-sm font-medium leading-none cursor-pointer flex-1">
                          {unity.name}
                        </span>
                      </div>
                    );
                  })}
                  {unities.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Nenhuma unidade encontrada
                    </p>
                  )}
                </div>
              </ScrollArea>
            )}
            {selectedUnities.length > 0 && (
              <p className="text-xs text-primary font-medium">
                {selectedUnities.length} unidade(s) selecionada(s)
              </p>
            )}
            {errors.unity_ids && (
              <p className="text-sm text-red-500">{errors.unity_ids.message}</p>
            )}
          </div>

          {/* Seção de Datas */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-base">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
              Período da Meta
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date" className="text-xs text-muted-foreground">Data de Início</Label>
                <Input type="date" {...register("start_date")} className="h-10" />
                {errors.start_date && (
                  <p className="text-xs text-red-500">
                    {errors.start_date.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="end_date" className="text-xs text-muted-foreground">Data de Fim</Label>
                <Input type="date" {...register("end_date")} className="h-10" />
                {errors.end_date && (
                  <p className="text-xs text-red-500">
                    {errors.end_date.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Seção de Dias Úteis */}
          {daysInPeriod.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-base">
                  <CalendarCheck className="h-4 w-4 text-muted-foreground" />
                  Dias Úteis
                  <span className="ml-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-primary/10 text-primary">
                    {selectedWorkingDays.length}
                  </span>
                </Label>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectWeekdays}
                    className="h-7 text-xs"
                  >
                    Seg-Sáb
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAllWorkingDays}
                    className="h-7 text-xs"
                  >
                    {selectedWorkingDays.length === daysInPeriod.length
                      ? "Desmarcar"
                      : "Todos"}
                  </Button>
                </div>
              </div>
              <ScrollArea className="h-[250px] border rounded-lg bg-muted/20">
                <div className="p-4 space-y-6">
                  {daysByMonth.map(({ month, year, days }) => (
                    <div key={`${year}-${month}`}>
                      <div className="text-sm font-semibold text-center mb-3 text-foreground">
                        {monthNames[month]} {year}
                      </div>
                      <div className="grid grid-cols-7 gap-1 mb-2">
                        {dayNames.map((name, index) => (
                          <div
                            key={name}
                            className={`text-center text-[10px] font-semibold py-1 ${
                              index === 0 ? "text-red-500" : "text-muted-foreground"
                            }`}
                          >
                            {name}
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {Array.from({ length: days[0]?.dayOfWeek || 0 }).map((_, i) => (
                          <div key={`empty-${month}-${i}`} className="h-9" />
                        ))}
                        {days.map((day) => {
                          const isSelected = selectedWorkingDays.includes(day.key);
                          const isSunday = day.dayOfWeek === 0;
                          return (
                            <button
                              key={day.key}
                              type="button"
                              onClick={() => handleWorkingDayToggle(day.key, !isSelected)}
                              className={`
                                h-9 w-full rounded-lg text-sm font-medium transition-all
                                ${isSelected
                                  ? "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90"
                                  : isSunday
                                    ? "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50"
                                    : "bg-background hover:bg-muted border border-border/50"
                                }
                              `}
                            >
                              {day.date.getDate()}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              {errors.working_days && (
                <p className="text-sm text-red-500">
                  {errors.working_days.message}
                </p>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isLoading || selectedUnities.length === 0}
              className="gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Criando...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Criar Meta
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
